import { motion, useReducedMotion } from 'framer-motion';
import { FC, MouseEvent, ReactElement, ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
	AwardRibbonOutline,
	BeakerOutline,
	Box,
	CelebrationStreakOutline,
	ChartBarOutline,
	ChatAlt2Outline,
	ChatAltOutline,
	CogOutline,
	DotsHorizontalOutline,
	HistoryOutline,
	HomeOutline,
	HStack,
	LeftDrawerCollapseOutline,
	LeftDrawerExpandOutline,
	rem,
	ShoppingCartOutline,
	Stack,
	TargetOutline,
	Text,
	Tooltip,
	useColorModeValue,
	useDisclosure,
	UsersOutline,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';
import { FeatureFlag, Permission, ProtectedContent, useCurrentCompany, useCurrentUser, useFeatureFlag, usePermission, Variant } from '@bonusly/who-am-i';

import { Bizy, useBizyFlightAnchor, useIsBizyFlightAnchorHidden, useReduceBizyMotionPreference } from '../../../components/bizy';
import { BizyEarlyAccessCard } from '../../../components/bizy-side-panel';
import { useBizyAskClick } from '../../../components/bizy-side-panel/hooks/use-bizy-ask-click';
import { useBizyNotificationAttention, useBizyUnreadCount } from '../../../components/bizy-side-panel/hooks/use-has-bizy-notification';
import { DynamicPromos } from '../../../components/promo/dynamic-promo';
import { useSeasonalTheme } from '../../../components/seasonal/seasonal-theme-context';
import { SeasonalToggle } from '../../../components/seasonal/seasonal-toggle';
import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { useCelebrationsPanel, useLeaderboardPanel, useNotificationsPanel, useRewardsPanel, useTeamPanel } from '../../../hooks/use-navbar-panels';
import { useNotificationFavicon } from '../../../hooks/use-notification-favicon';
import { useNotificationTitle } from '../../../hooks/use-notification-title';
import { AnalyticsEvent } from '../../../lib/analytics/events';
import { useAnalytics } from '../../../lib/analytics/use-analytics';
import { loadHighlights } from '../../../queries/rewards';
import { normalizeTwoColumnLayout } from '../../../utils/normalize-two-column-layout';
import { AppPath } from '../../admin-navbar/config/router';
import { useTeamTracker } from '../../bonus-feed/features/recognition/components/tracker-widget/hooks';
import { useCurrentUserReports } from '../../team-management/hooks/use-current-user-reports';
import { useFetchNotifications } from '../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { SidebarItemId } from '../config/sidebar-items';
import { useAdminNavLink } from '../hooks/use-admin-nav-link';
import { useHasNoTeamContent } from '../hooks/use-has-no-team-content';
import { useSidebarPreferences } from '../hooks/use-sidebar-preferences';
import { useTeamPanelBadge } from '../hooks/use-team-panel-badge';
import { AnnouncementMenuItem } from './announcement-menu-item';
import { CelebrationsPanel } from './celebrations';
import { CompanyLogo } from './company-logo';
import { CreateNewMenu } from './create-new-menu';
import { CustomizeSidebarModal } from './customize-sidebar-modal';
import { DarkModeAlert, DarkModeAlertRef } from './dark-mode-alert';
import { IconAndTextContent } from './icon-and-text-content';
import { LeaderboardPanel } from './leaderboard';
import { NotificationPanel } from './notification';
import { OrgSupportCard } from './org-support-card';
import { RewardsHighlightsPanel } from './rewards-highlights-panel';
import { SearchNavItem } from './search-nav-item';
import { TeamPanel } from './team/team-panel';
import { TrialCard } from './trial-card';
import { UserMenu } from './user-menu';
import { UserNavAction } from './user-nav-action';
import { UserNavDivider } from './user-nav-divider';
import { UserNavLink } from './user-nav-link';
import { UserNavMenu } from './user-nav-menu';

interface UserNavbarProps {
	isExpanded: boolean;
	toggleSidebar: () => void;
	showBizyToggle?: boolean;
	isBizyPanelOpen?: boolean;
}

type SidebarLayout = {
	mainNavItems: SidebarItemId[];
	moreMenuItems: SidebarItemId[];
};

export const UserNavbar: FC<UserNavbarProps> = ({ isExpanded, toggleSidebar, showBizyToggle = false, isBizyPanelOpen = false }) => {
	const { t } = useTranslation('navigation');
	const { t: c } = useTranslation('common');
	const { track } = useAnalytics();
	const toast = useToast();
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const user = useCurrentUser();
	const isAdmin = user.global_admin;
	const company = useCurrentCompany();
	const { hide_competitive_features: hideCompetitiveFeatures } = company;
	const hideLeaderboard = hideCompetitiveFeatures && !isAdmin;
	const isRemoveHomepageWidgetColumnEnabled = useFeatureFlag(Variant.RemoveHomepageWidgetColumn);
	const hasLabs = useFeatureFlag('bonusly-labs');
	const themeGradient = useColorModeValue(
		'linear-gradient(to top, var(--chakra-colors-primary-bgAlpha), transparent 90%)',
		'linear-gradient(to top, var(--chakra-colors-primary-bgAlpha), transparent 60%)'
	);
	const canAccessTeamHub = usePermission(Permission.CanAccessTeamHub);
	const canAccessOneOnOnes = usePermission(Permission.CanAccessOneOnOnes);
	const canAccessRecaps = usePermission(Permission.CanAccessRecaps);
	const canAccessAdvice = usePermission(Permission.CanAccessAdvice);
	const canAccessGoals = usePermission(Permission.CanAccessGoals);
	const canAccessIncentivesExperience = usePermission(Permission.CanAccessIncentivesExperience);
	const canAccessCelebrationsExperience = usePermission(Permission.CanAccessCelebrationsExperience);
	const canAccessAnalytics = usePermission(Permission.CanAccessAnalytics);
	const {
		mainNavItems: configuredMainNavItems,
		moreMenuItems: configuredMoreMenuItems,
		availableItemIds: configuredAvailableSidebarItemIds,
		hasCachedLayout: hasCachedSidebarLayout,
		source: sidebarPreferencesSource,
		isLoading: isSidebarPreferencesLoading,
		savePreferences,
		resetPreferences,
		isSaving: isSidebarPreferencesSaving,
	} = useSidebarPreferences();
	const customizeSidebarModal = useDisclosure();
	const [previewSidebarLayout, setPreviewSidebarLayout] = useState<SidebarLayout | null>(null);
	const [isBizyNavHovered, setIsBizyNavHovered] = useState(false);
	const shouldReduceMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();
	const shouldReduceBizyMotion = shouldReduceMotion || reduceBizyMotion;
	const navBizyAnchorRef = useBizyFlightAnchor('navFallback');
	const isNavBizyHidden = useIsBizyFlightAnchorHidden('navFallback');
	// Drives the waving Bizy + red dot when Bizy is allowed to use the
	// strongest attention state. Received-recognition notifications take
	// visual precedence so being recognized stays the primary signal.
	const { shouldSurface: shouldSurfaceBizyNotification, unreadCount: surfacedBizyUnreadCount } = useBizyNotificationAttention();
	const bizyUnreadCount = useBizyUnreadCount();
	const unreadBizyMotion = shouldSurfaceBizyNotification && !shouldReduceBizyMotion;
	const handleBizyClick = useBizyAskClick();

	const homeURL = isRecognize ? monarchURL(AppPath.Home) : AppPath.Home;
	const adminNavLink = useAdminNavLink();
	const { unreadCount } = useFetchNotifications(user.locale);
	const { data: directReports = [] } = useCurrentUserReports();

	// Surface unread Bonusly + Bizy notifications in the browser tab (favicon
	// badge + (N) prefix in the title) so they're visible even when the Bonusly
	// tab is in the background.
	const totalUnreadCount = unreadCount + bizyUnreadCount;
	useNotificationFavicon(totalUnreadCount > 0);
	useNotificationTitle(totalUnreadCount);

	// Panel states (managed via zustand)
	const notificationsPanel = useNotificationsPanel();
	const teamPanel = useTeamPanel();
	const leaderboardPanel = useLeaderboardPanel();
	const celebrationsPanel = useCelebrationsPanel();
	const rewardsPanel = useRewardsPanel();

	// Hide celebrations nav item when the company has not set up any celebrations or the feature is toggled off
	const hideCelebrations = !company?.has_celebrations || !canAccessCelebrationsExperience;

	const { data: people, isError, isLoading: isLoadingTrackerData } = useTeamTracker({ enabled: isRemoveHomepageWidgetColumnEnabled });
	const canManageTeamRequests = usePermission(Permission.CanManageTeamRequests);
	const isTrialCompany = company?.isTrial;
	const { showBadge: showTeamBadge } = useTeamPanelBadge();
	const hasNoTeamContent = useHasNoTeamContent();
	const showTeamMenu =
		canAccessTeamHub || canManageTeamRequests || (isRemoveHomepageWidgetColumnEnabled && !isLoadingTrackerData && !isError && people && people.length > 0);

	const canSeeCelebrations = isRemoveHomepageWidgetColumnEnabled && !hideCelebrations;

	const runtimeAvailableSidebarItemIds: SidebarItemId[] = useMemo(
		() => [
			...(canAccessOneOnOnes ? (['oneOnOnes'] as const) : []),
			...(canAccessRecaps ? (['recaps'] as const) : []),
			...(canAccessAdvice ? (['advice'] as const) : []),
			...(canAccessGoals ? (['goals'] as const) : []),
			...(showTeamMenu ? (['team'] as const) : []),
			'rewards',
			...(canAccessIncentivesExperience ? (['incentives'] as const) : []),
			...(canAccessAnalytics && !hideLeaderboard ? (['leaderboard'] as const) : []),
			...(canSeeCelebrations ? (['celebrations'] as const) : []),
		],
		[
			canAccessAdvice,
			canAccessAnalytics,
			canAccessGoals,
			canAccessCelebrationsExperience,
			canAccessIncentivesExperience,
			canAccessOneOnOnes,
			canAccessRecaps,
			canSeeCelebrations,
			hideLeaderboard,
			showTeamMenu,
		]
	);

	const availableSidebarItemIds = useMemo(() => {
		const apiAvailableItemIds = new Set(configuredAvailableSidebarItemIds);
		return runtimeAvailableSidebarItemIds.filter((itemId) => apiAvailableItemIds.has(itemId));
	}, [configuredAvailableSidebarItemIds, runtimeAvailableSidebarItemIds]);

	const sidebarLayout = useMemo(() => {
		// While the customization modal is open, always render the draft layout so
		// users see immediate background updates for all reorder operations.
		if (customizeSidebarModal.isOpen && previewSidebarLayout) {
			const previewLayout = normalizeTwoColumnLayout(previewSidebarLayout.mainNavItems, previewSidebarLayout.moreMenuItems, availableSidebarItemIds);
			return {
				mainNavItems: previewLayout.mainItems,
				moreMenuItems: previewLayout.moreItems,
			} satisfies SidebarLayout;
		}

		const normalizedLayout = normalizeTwoColumnLayout(configuredMainNavItems, configuredMoreMenuItems, availableSidebarItemIds);
		return {
			mainNavItems: normalizedLayout.mainItems,
			moreMenuItems: normalizedLayout.moreItems,
		} satisfies SidebarLayout;
	}, [availableSidebarItemIds, configuredMainNavItems, configuredMoreMenuItems, customizeSidebarModal.isOpen, previewSidebarLayout]);

	const isHydratingSidebarOrder = isSidebarPreferencesLoading && !hasCachedSidebarLayout;
	const orderedMainSidebarItemIds = useMemo(
		() => (isHydratingSidebarOrder ? [] : sidebarLayout.mainNavItems),
		[isHydratingSidebarOrder, sidebarLayout.mainNavItems]
	);
	const orderedMoreSidebarItemIds = useMemo(
		() => (isHydratingSidebarOrder ? [] : sidebarLayout.moreMenuItems),
		[isHydratingSidebarOrder, sidebarLayout.moreMenuItems]
	);
	const hasVisibleMoreMenuItems = orderedMoreSidebarItemIds.length > 0;

	const bizyNavAction = showBizyToggle ? (
		<motion.div
			initial={false}
			animate={{ height: isBizyPanelOpen ? 0 : 'auto', opacity: isBizyPanelOpen ? 0 : 1 }}
			transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
			style={{ overflow: 'hidden', width: '100%' }}
			onMouseEnter={() => setIsBizyNavHovered(true)}
			onMouseLeave={() => setIsBizyNavHovered(false)}
		>
			<UserNavAction
				onClick={handleBizyClick}
				data-testid="nav-action-bizy-toggle"
				label={shouldSurfaceBizyNotification ? `${t('links.bizyUnread')} (/)` : `${t('links.bizy')} (/)`}
				isExpanded={isExpanded}
			>
				<IconAndTextContent
					icon={
						<Box ref={navBizyAnchorRef} display="flex" alignItems="center" justifyContent="center" w={6} h={5} flexShrink={0}>
							<motion.div
								initial={false}
								animate={{ opacity: isNavBizyHidden ? 0 : 1, scale: shouldReduceBizyMotion ? 1 : isNavBizyHidden ? 0.6 : 1 }}
								transition={{ duration: shouldReduceBizyMotion ? 0 : 0.22, ease: [0.4, 0, 0.2, 1] }}
								style={{ display: 'flex', transformOrigin: 'bottom center' }}
							>
								<motion.div
									animate={unreadBizyMotion ? { rotate: [0, -14, 14, -14, 14, 0], scale: [1, 1.08, 1.08, 1.08, 1.08, 1] } : { rotate: 0, scale: 1 }}
									transition={unreadBizyMotion ? { duration: 1.4, repeat: Infinity, repeatDelay: 1.6, ease: 'easeInOut' } : { duration: 0.2 }}
									style={{ display: 'flex', transformOrigin: 'bottom center' }}
								>
									<Bizy state={isBizyNavHovered || shouldSurfaceBizyNotification ? 'waving' : 'curious'} size={44} />
								</motion.div>
							</motion.div>
						</Box>
					}
					label={t('links.bizy')}
					isExpanded={isExpanded}
					shortcut="/"
					badge={
						shouldSurfaceBizyNotification ? (
							<Box
								minWidth={rem(18)}
								height={rem(18)}
								px={1.5}
								borderRadius="full"
								bg="error.icon"
								color="text.onColor"
								display="inline-flex"
								alignItems="center"
								justifyContent="center"
								fontSize="xs"
								fontWeight="bold"
								lineHeight={1}
							>
								{surfacedBizyUnreadCount > 9 ? '9+' : surfacedBizyUnreadCount}
							</Box>
						) : null
					}
				/>
			</UserNavAction>
		</motion.div>
	) : null;

	// Scrollable nav overflow detection
	const scrollableNavRef = useRef<HTMLDivElement>(null);
	const hasOverflow = useHasOverflow(scrollableNavRef);

	// Dark mode alert ref for dismissing when user menu is opened
	const darkModeAlertRef = useRef<DarkModeAlertRef>(null);

	// Handle click for cross-domain navigation
	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (isRecognize) {
			e.preventDefault();
			window.location.href = homeURL;
		}
	};

	const handleRewardsClick = async () => {
		track(AnalyticsEvent.UserNavbar.RewardsNavigation);

		if (rewardsPanel.isOpen) {
			rewardsPanel.close();
		} else {
			await loadHighlights();
			rewardsPanel.open();
		}
	};

	const handleRewardsHover = () => {
		if (!rewardsPanel.isOpen) {
			loadHighlights();
		}
	};

	const handleTeamClick = () => {
		track(AnalyticsEvent.UserNavbar.TeamNavigation, { isManager: user.is_manager });
		teamPanel.toggle();
	};

	const handleLeaderboardClick = () => {
		track(AnalyticsEvent.UserNavbar.LeaderboardNavigation);
		leaderboardPanel.toggle();
	};

	const handleCelebrationsClick = () => {
		track(AnalyticsEvent.UserNavbar.CelebrationsNavigation);
		celebrationsPanel.toggle();
	};

	const handleOpenCustomizeSidebar = () => {
		track(AnalyticsEvent.UserNavbar.CustomizeSidebarOpened);
		setPreviewSidebarLayout({
			mainNavItems: sidebarLayout.mainNavItems,
			moreMenuItems: sidebarLayout.moreMenuItems,
		});
		customizeSidebarModal.onOpen();
	};

	const handleCloseCustomizeSidebar = () => {
		setPreviewSidebarLayout(null);
		customizeSidebarModal.onClose();
	};

	const handlePreviewSidebarChange = (mainNavItems: SidebarItemId[], moreMenuItems: SidebarItemId[]) => {
		setPreviewSidebarLayout({
			mainNavItems,
			moreMenuItems,
		});
	};

	const handleSaveSidebarPreferences = async (mainNavItems: SidebarItemId[], moreMenuItems: SidebarItemId[]) => {
		try {
			await savePreferences(mainNavItems, moreMenuItems);
			track(AnalyticsEvent.Navigation.UserPreferencesSaved, {
				main_nav_count: mainNavItems.length,
				more_menu_count: moreMenuItems.length,
			});
			toast({
				title: t('customizeSidebar.toast.saveSuccessTitle'),
				description: t('customizeSidebar.toast.saveSuccessDescription'),
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: t('customizeSidebar.toast.saveErrorTitle'),
				description: error instanceof Error ? error.message : t('customizeSidebar.toast.saveErrorDescription'),
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	};

	const handleResetSidebarPreferences = async () => {
		try {
			const resetLayout = await resetPreferences();
			setPreviewSidebarLayout({
				mainNavItems: resetLayout.mainNavItems,
				moreMenuItems: resetLayout.moreMenuItems,
			});
			track(AnalyticsEvent.Navigation.UserPreferencesReset);
			toast({
				title: t('customizeSidebar.toast.resetSuccessTitle'),
				description: t('customizeSidebar.toast.resetSuccessDescription'),
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			return resetLayout;
		} catch (error) {
			toast({
				title: t('customizeSidebar.toast.resetErrorTitle'),
				description: error instanceof Error ? error.message : t('customizeSidebar.toast.resetErrorDescription'),
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			throw error;
		}
	};

	const renderMainSidebarItem = (itemId: SidebarItemId) => {
		switch (itemId) {
			case 'oneOnOnes':
				return (
					<UserNavLink
						key={itemId}
						to="/meet"
						onClick={() => track(AnalyticsEvent.UserNavbar.OneOnOnesNavigation)}
						data-testid="nav-link-one-on-ones"
						label={t('links.oneOnOnes')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent icon={<ChatAlt2Outline size="md" />} label={t('links.oneOnOnes')} isExpanded={isExpanded} />
					</UserNavLink>
				);
			case 'goals':
				return (
					<UserNavLink
						key={itemId}
						to="/growth/goals"
						onClick={() => track(AnalyticsEvent.UserNavbar.GoalsNavigation)}
						data-testid="nav-link-goals"
						label={t('links.goals')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent icon={<TargetOutline size="md" />} label={t('links.goals')} isExpanded={isExpanded} />
					</UserNavLink>
				);
			case 'recaps':
				return (
					<UserNavLink
						key={itemId}
						to="/growth/recap-review"
						onClick={() => track(AnalyticsEvent.UserNavbar.RecapsNavigation)}
						data-testid="nav-link-recaps"
						label={t('links.recaps')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent icon={<HistoryOutline size="md" />} label={t('links.recaps')} isExpanded={isExpanded} />
					</UserNavLink>
				);
			case 'team':
				return (
					<UserNavAction
						key={itemId}
						onClick={handleTeamClick}
						isActive={teamPanel.isOpen}
						data-testid="nav-link-team"
						label={t('links.team')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent
							icon={
								canManageTeamRequests && showTeamBadge && hasNoTeamContent && !isTrialCompany ? (
									<Box position="relative" display="flex" alignItems="center">
										<UsersOutline size="md" />
										<Box position="absolute" top={rem(-4)} right={rem(-4)} h={rem(10)} w={rem(10)} bg="error.inputBorder" borderRadius="full" />
									</Box>
								) : (
									<UsersOutline size="md" />
								)
							}
							label={t('links.team')}
							isExpanded={isExpanded}
						/>
					</UserNavAction>
				);
			case 'rewards':
				return (
					<UserNavAction
						key={itemId}
						onClick={handleRewardsClick}
						onMouseEnter={handleRewardsHover}
						isActive={rewardsPanel.isOpen}
						data-testid="nav-link-rewards"
						label={t('links.rewards')}
						isExpanded={isExpanded}
					>
						<IconAndChildContent icon={<ShoppingCartOutline size="md" />} isExpanded={isExpanded}>
							<HStack justifyContent="space-between" alignItems="flex-start" width="100%">
								<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
									{t('links.rewards')}
								</Text>
								<Box bgColor="points.bgAlpha" fontWeight="semibold" fontSize={1} color="points.fg" borderRadius="full" px={rem(6)} py={rem(3)}>
									+{c('intl_number', { value: user.earning_balance })}
								</Box>
							</HStack>
						</IconAndChildContent>
					</UserNavAction>
				);
			case 'incentives':
				return (
					<UserNavLink
						key={itemId}
						to="/incentives"
						onClick={() => track(AnalyticsEvent.UserNavbar.IncentivesNavigation)}
						data-testid="nav-link-incentives"
						label={t('links.incentives')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent icon={<AwardRibbonOutline size="md" />} label={t('links.incentives')} isExpanded={isExpanded} />
					</UserNavLink>
				);
			case 'leaderboard':
				return (
					<UserNavAction
						key={itemId}
						onClick={handleLeaderboardClick}
						isActive={leaderboardPanel.isOpen}
						data-testid="nav-link-leaderboard"
						label={t('links.leaderboard')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded={isExpanded} />
					</UserNavAction>
				);
			case 'celebrations':
				return (
					<UserNavAction
						key={itemId}
						onClick={handleCelebrationsClick}
						isActive={celebrationsPanel.isOpen}
						data-testid="nav-action-celebrations"
						label={t('links.celebrations')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent icon={<CelebrationStreakOutline size="md" />} label={t('links.celebrations')} isExpanded={isExpanded} />
					</UserNavAction>
				);
			case 'advice':
				return (
					<UserNavLink
						key={itemId}
						to="/growth/feedback"
						onClick={() => track(AnalyticsEvent.UserNavbar.AdviceNavigation)}
						data-testid="nav-link-feedback"
						label={t('links.feedback')}
						isExpanded={isExpanded}
					>
						<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('links.feedback')} isExpanded={isExpanded} />
					</UserNavLink>
				);
			default:
				return null;
		}
	};

	const renderMoreSidebarItem = (itemId: SidebarItemId) => {
		switch (itemId) {
			case 'oneOnOnes':
				return (
					<UserNavLink key={itemId} to="/meet" onClick={() => track(AnalyticsEvent.UserNavbar.OneOnOnesNavigation)} data-testid="nav-link-one-on-ones">
						<IconAndTextContent icon={<ChatAlt2Outline size="md" />} label={t('links.oneOnOnes')} isExpanded />
					</UserNavLink>
				);
			case 'goals':
				return (
					<UserNavLink key={itemId} to="/growth/goals" onClick={() => track(AnalyticsEvent.UserNavbar.GoalsNavigation)} data-testid="nav-link-goals">
						<IconAndTextContent icon={<TargetOutline size="md" />} label={t('links.goals')} isExpanded />
					</UserNavLink>
				);
			case 'recaps':
				return (
					<UserNavLink key={itemId} to="/growth/recap-review" onClick={() => track(AnalyticsEvent.UserNavbar.RecapsNavigation)} data-testid="nav-link-recaps">
						<IconAndTextContent icon={<HistoryOutline size="md" />} label={t('links.recaps')} isExpanded />
					</UserNavLink>
				);
			case 'team':
				return (
					<UserNavAction key={itemId} onClick={handleTeamClick} isActive={teamPanel.isOpen} data-testid="nav-link-team" label={t('links.team')} isExpanded>
						<IconAndTextContent
							icon={
								canManageTeamRequests && showTeamBadge && hasNoTeamContent && !isTrialCompany ? (
									<Box position="relative" display="flex" alignItems="center">
										<UsersOutline size="md" />
										<Box position="absolute" top={rem(-4)} right={rem(-4)} h={rem(10)} w={rem(10)} bg="error.inputBorder" borderRadius="full" />
									</Box>
								) : (
									<UsersOutline size="md" />
								)
							}
							label={t('links.team')}
							isExpanded
						/>
					</UserNavAction>
				);
			case 'rewards':
				return (
					<UserNavAction
						key={itemId}
						onClick={handleRewardsClick}
						onMouseEnter={handleRewardsHover}
						isActive={rewardsPanel.isOpen}
						data-testid="nav-link-rewards"
						label={t('links.rewards')}
						isExpanded
					>
						<IconAndTextContent icon={<ShoppingCartOutline size="md" />} label={t('links.rewards')} isExpanded />
					</UserNavAction>
				);
			case 'incentives':
				return (
					<UserNavLink key={itemId} to="/incentives" onClick={() => track(AnalyticsEvent.UserNavbar.IncentivesNavigation)} data-testid="nav-link-incentives">
						<IconAndTextContent icon={<AwardRibbonOutline size="md" />} label={t('links.incentives')} isExpanded />
					</UserNavLink>
				);
			case 'leaderboard':
				return (
					<UserNavAction
						key={itemId}
						onClick={handleLeaderboardClick}
						isActive={leaderboardPanel.isOpen}
						data-testid="nav-link-leaderboard"
						label={t('links.leaderboard')}
						isExpanded
					>
						<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded />
					</UserNavAction>
				);
			case 'celebrations':
				return (
					<UserNavAction key={itemId} onClick={handleCelebrationsClick} isActive={celebrationsPanel.isOpen} data-testid="nav-action-celebrations">
						<IconAndTextContent icon={<CelebrationStreakOutline size="md" />} label={t('links.celebrations')} isExpanded />
					</UserNavAction>
				);
			case 'advice':
				return (
					<UserNavLink key={itemId} to="/growth/feedback" onClick={() => track(AnalyticsEvent.UserNavbar.AdviceNavigation)} data-testid="nav-link-feedback">
						<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('links.feedback')} isExpanded />
					</UserNavLink>
				);
			default:
				return null;
		}
	};

	return (
		<>
			<VStack
				bgColor={isSeasonalThemeEnabled ? 'bg.minipage' : 'bg.canvas'}
				backdropFilter={isSeasonalThemeEnabled ? 'blur(44px)' : undefined}
				backgroundImage={!isSeasonalThemeEnabled ? themeGradient : 'none'}
				w="full"
				h="100%"
				px={rem(8)}
				py={rem(20)}
				spacing={0}
				align="flex-start"
			>
				<HStack width="100%" mb={rem(15)} spacing={0} position="relative">
					<Box
						as={Link}
						id="primary-nav-logo"
						flex={1}
						minW={0}
						maxW={rem(200)}
						pr={rem(40)}
						display="flex"
						alignItems="center"
						justifyContent="flex-start"
						to={homeURL}
						onClick={handleLogoClick}
						opacity={isExpanded ? 1 : 0}
						overflow="hidden"
						pointerEvents={isExpanded ? 'auto' : 'none'}
						transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4,0,.2,1)'}
					>
						<CompanyLogo context="sidebar" />
					</Box>

					<Tooltip label={isExpanded ? t('collapseSidebar') : t('expandSidebar')} placement="right" openDelay={900}>
						<Box
							as="button"
							aria-label={isExpanded ? t('collapseSidebar') : t('expandSidebar')}
							display="flex"
							alignItems="center"
							justifyContent="center"
							p={3}
							borderRadius="md"
							cursor="pointer"
							color="text.softer"
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={toggleSidebar}
							position="absolute"
							right={0}
						>
							{isExpanded ? (
								<LeftDrawerCollapseOutline data-testid="collapse-sidebar" size="sm" />
							) : (
								<LeftDrawerExpandOutline data-testid="expand-sidebar" size="sm" />
							)}
						</Box>
					</Tooltip>
				</HStack>

				<Stack as="nav" spacing={2} flexDir="column" width="100%" flex={1} minHeight={0}>
					{/* Scrollable nav items */}
					<Box
						ref={scrollableNavRef}
						flex={1}
						overflowY="auto"
						overflowX="hidden"
						minHeight={0}
						borderBottom={hasOverflow ? '1px solid' : 'none'}
						borderColor="border.default"
						mx={rem(-8)}
						px={rem(8)}
						pb={hasOverflow ? rem(8) : 0}
						sx={{
							'&::-webkit-scrollbar': { width: '6px' },
							'&::-webkit-scrollbar-track': { background: 'transparent' },
							'&::-webkit-scrollbar-thumb': {
								background: 'scrollbar.thumb',
								borderRadius: '3px',
							},
							'&::-webkit-scrollbar-thumb:hover': { background: 'scrollbar.thumbHover' },
							scrollbarColor: 'var(--chakra-colors-scrollbar-thumb) transparent',
							scrollbarWidth: 'thin',
						}}
					>
						<VStack spacing={1} align="stretch">
							{/* ANNOUNCEMENTS */}
							<FeatureFlag flag={Variant.RemoveHomepageWidgetColumn}>
								<ProtectedContent permissions={Permission.CanAccessAnnouncements}>
									<AnnouncementMenuItem isExpanded={isExpanded} />
								</ProtectedContent>
							</FeatureFlag>

							{/* HOME */}
							<UserNavLink
								to="/"
								onClick={() => track(AnalyticsEvent.UserNavbar.HomeNavigation)}
								data-testid="nav-link-home"
								label={t('links.home')}
								isExpanded={isExpanded}
							>
								<IconAndTextContent icon={<HomeOutline size="md" />} label={t('links.home')} isExpanded={isExpanded} />
							</UserNavLink>

							{/* SEARCH */}
							<SearchNavItem isExpanded={isExpanded} />

							{orderedMainSidebarItemIds.map(renderMainSidebarItem)}

							{/* MORE MENU ITEMS */}
							<UserNavMenu
								buttonIcon={<DotsHorizontalOutline size="md" />}
								buttonText={t('moreButtonText')}
								isExpanded={isExpanded}
								menuListZIndex="overlay"
								onOpen={() => track(AnalyticsEvent.Navigation.MoreMenuOpened)}
							>
								{orderedMoreSidebarItemIds.map(renderMoreSidebarItem)}
								{hasVisibleMoreMenuItems && <UserNavDivider />}
								<UserNavAction onClick={handleOpenCustomizeSidebar} data-testid="nav-action-customize-sidebar" label={t('customizeSidebar.title')} isExpanded>
									<IconAndTextContent icon={<CogOutline size="md" />} label={t('customizeSidebar.title')} isExpanded />
								</UserNavAction>
							</UserNavMenu>

							{/* CREATE NEW MENU */}
							<CreateNewMenu isExpanded={isExpanded} />
						</VStack>
					</Box>

					{/* Fixed bottom items - always visible */}
					<VStack spacing={1} flexShrink={0}>
						{/* TRIAL CARD */}
						<TrialCard isExpanded={isExpanded} />

						{/* ORG SUPPORT CARD */}
						<OrgSupportCard isExpanded={isExpanded} />

						<DynamicPromos isSidebarExpanded={isExpanded} />
						<SeasonalToggle isExpanded={isExpanded} />

						{bizyNavAction}

						{/* BIZY EARLY-ACCESS OPT-IN (BIZY-322) — shown only to eligible
						    Org customers; self-gates via useIsBizyEarlyAccessEligible.
						    Collapsed rail is too narrow for the card, so expanded-only. */}
						{isExpanded && <BizyEarlyAccessCard />}

						{/* LABS */}
						{hasLabs && (
							<UserNavLink
								to="/labs"
								onClick={() => track(AnalyticsEvent.UserNavbar.LabsNavigation)}
								data-testid="nav-link-labs"
								label={t('links.labs')}
								isExpanded={isExpanded}
							>
								<IconAndChildContent icon={<BeakerOutline size="md" />} isExpanded={isExpanded}>
									<HStack justifyContent="space-between" alignItems="center" width="100%">
										<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
											{t('links.labs')}
										</Text>
										<Box bg="points.bgAlpha" color="points.fg" px={2} py={0.5} borderRadius="full" fontSize={50} fontWeight="semibold">
											{t('links.labsAlpha')}
										</Box>
									</HStack>
								</IconAndChildContent>
							</UserNavLink>
						)}

						{/* ADMIN MENU */}
						<ProtectedContent permissions={Permission.CanViewAdmin}>
							<UserNavLink
								to={adminNavLink.to}
								isRecognizeRoute={adminNavLink.isRecognizeRoute}
								onClick={() => track(AnalyticsEvent.UserNavbar.AdminNavigation)}
								data-testid="nav-link-admin"
								label={t('links.adminSettings')}
								isExpanded={isExpanded}
							>
								<IconAndTextContent icon={<CogOutline size="md" />} label={t('links.adminSettings')} isExpanded={isExpanded} />
							</UserNavLink>
						</ProtectedContent>

						{/* USER MENU */}
						<Box position="relative" width="100%">
							<DarkModeAlert ref={darkModeAlertRef} isExpanded={isExpanded} />
							<UserMenu
								isExpanded={isExpanded}
								onOpenNotifications={notificationsPanel.open}
								onMenuOpen={() => darkModeAlertRef.current?.dismiss()}
								unreadCount={unreadCount}
							/>
						</Box>
					</VStack>
				</Stack>

				{/* Notification Panel */}
				<NotificationPanel isOpen={notificationsPanel.isOpen} onClose={notificationsPanel.close} isSidebarExpanded={isExpanded} />

				{/* Team Panel */}
				<TeamPanel isOpen={teamPanel.isOpen} onClose={teamPanel.close} isSidebarExpanded={isExpanded} directReports={directReports} people={people} />

				{/* Leaderboard Panel */}
				<LeaderboardPanel isOpen={leaderboardPanel.isOpen} onClose={leaderboardPanel.close} isSidebarExpanded={isExpanded} />

				{/* Celebrations Panel */}
				<CelebrationsPanel isOpen={celebrationsPanel.isOpen} onClose={celebrationsPanel.close} isSidebarExpanded={isExpanded} />

				<RewardsHighlightsPanel isOpen={rewardsPanel.isOpen} onClose={rewardsPanel.close} isSidebarExpanded={isExpanded} />

				<CustomizeSidebarModal
					isOpen={customizeSidebarModal.isOpen}
					onClose={handleCloseCustomizeSidebar}
					availableItemIds={availableSidebarItemIds}
					mainNavItems={sidebarLayout.mainNavItems}
					moreMenuItems={sidebarLayout.moreMenuItems}
					source={sidebarPreferencesSource}
					isSaving={isSidebarPreferencesSaving}
					onSave={handleSaveSidebarPreferences}
					onReset={handleResetSidebarPreferences}
					onChange={handlePreviewSidebarChange}
				/>
			</VStack>
		</>
	);
};

type IconAndChildContentProps = {
	icon: ReactElement;
	isExpanded: boolean;
	children: ReactNode;
};

const IconAndChildContent: FC<IconAndChildContentProps> = ({ children, icon, isExpanded }) => (
	<HStack justifyContent="flex-start" alignItems="flex-start" spacing={3} w="100%">
		{icon}
		<Box opacity={isExpanded ? 1 : 0} transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4, 0, .2, 1)'} flex={1}>
			{children}
		</Box>
	</HStack>
);

function useHasOverflow(ref: RefObject<HTMLDivElement>) {
	const [hasOverflow, setHasOverflow] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const checkOverflow = () => {
			setHasOverflow(element.scrollHeight > element.clientHeight);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [ref]);

	return hasOverflow;
}
